type PageArticleInlineMetrics = {
  viewsFromPage: number
  uniqueReaders: number
  avgTimeMs: number
  avgScrollDepth: number
}

type PageSlotMetricRow = {
  articleId: string
  title: string
  viewsFromPage: number
}

type PageInlineMetricsCardProps = {
  metrics: PageArticleInlineMetrics
  anchor?: 'left' | 'right' | 'inline'
  variant?: 'full' | 'mini'
}

type PageSlotInlineMetricsCardProps = {
  rows: PageSlotMetricRow[]
  anchor?: 'left' | 'right' | 'inline'
  totalPageViews?: number
  blockId?: string
  activeBlockId?: string | null
  activeArticleId?: string | null
}

function fmtDuration(ms: number): string {
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds % 3600) / 60)
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
}

function cardClasses(anchor: 'left' | 'right' | 'inline'): string {
  if (anchor === 'inline') return 'mt-2 w-full'
  if (anchor === 'left') return 'hidden xl:block absolute right-[calc(100%+1rem)] top-0 w-48'
  return 'hidden xl:block absolute left-[calc(100%+1rem)] top-0 w-48'
}

function truncate(value: string, length: number): string {
  return value.length > length ? `${value.slice(0, length - 1)}…` : value
}

export type { PageArticleInlineMetrics, PageSlotMetricRow }

export function PageInlineMetricsCard({
  metrics,
  anchor = 'right',
  variant = 'full',
}: PageInlineMetricsCardProps) {
  const labelClass = variant === 'mini' ? 'text-[9px]' : 'text-[10px]'
  const valueClass = variant === 'mini' ? 'text-[11px]' : 'text-xs'

  return (
    <aside className={cardClasses(anchor)}>
      <div className="rounded-lg border border-line bg-surface p-2.5 shadow-sm">
        <p className="mb-2 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
          Page Metrics
        </p>
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className={`${labelClass} text-fg-secondary`}>Attributed views</span>
            <span className={`${valueClass} font-semibold tabular-nums text-fg`}>{metrics.viewsFromPage.toLocaleString()}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className={`${labelClass} text-fg-secondary`}>Readers</span>
            <span className={`${valueClass} font-semibold tabular-nums text-fg`}>{metrics.uniqueReaders.toLocaleString()}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className={`${labelClass} text-fg-secondary`}>Avg time</span>
            <span className={`${valueClass} font-semibold tabular-nums text-fg`}>{fmtDuration(metrics.avgTimeMs)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-3">
            <span className={`${labelClass} text-fg-secondary`}>Avg scroll</span>
            <span className={`${valueClass} font-semibold tabular-nums text-fg`}>{metrics.avgScrollDepth}%</span>
          </div>
        </div>
      </div>
    </aside>
  )
}

export function PageSlotInlineMetricsCard({
  rows,
  anchor = 'right',
  totalPageViews = 0,
  blockId,
  activeBlockId,
  activeArticleId,
}: PageSlotInlineMetricsCardProps) {
  const totalViews = rows.reduce((sum, row) => sum + row.viewsFromPage, 0)
  const totalPct = totalPageViews > 0 ? Math.round((totalViews / totalPageViews) * 1000) / 10 : 0
  const blockIsActive = Boolean(blockId && activeBlockId === blockId)
  const cardIsDimmed = Boolean(activeBlockId && !blockIsActive)

  return (
    <aside className={cardClasses(anchor)}>
      <div className={[
        'rounded-lg border border-line bg-surface p-2.5 shadow-sm transition-all duration-200',
        blockIsActive ? 'ring-2 ring-accent-primary-solid bg-accent-primary-fill/40 shadow-md' : '',
        cardIsDimmed ? 'opacity-35 saturate-50' : '',
      ].join(' ')}>
        <div className="mb-2 flex items-end justify-between gap-3 border-b border-line pb-2">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Attributed Traffic</p>
            <p className="mt-0.5 text-[10px] text-fg-secondary">{totalPct.toFixed(1)}% of page-attributed views</p>
          </div>
          <p className="text-sm font-semibold tabular-nums text-fg">{totalViews.toLocaleString()}</p>
        </div>
        <div className="space-y-1.5">
          {rows.map(row => (
            <div
              key={row.articleId}
              className={[
                'flex items-start justify-between gap-3 rounded px-1 py-0.5 transition-all duration-200',
                activeArticleId === row.articleId ? 'bg-accent-primary-fill/60 ring-1 ring-accent-primary-solid' : '',
                activeArticleId && activeArticleId !== row.articleId ? 'opacity-35' : '',
              ].join(' ')}
            >
              <p className="min-w-0 text-[10px] leading-4 text-fg-secondary">{truncate(row.title, 36)}</p>
              <p className="shrink-0 text-[11px] font-semibold tabular-nums text-fg">{row.viewsFromPage.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>
    </aside>
  )
}